import graphql__f5c1293509ea94c6ccbc from "./__generated__/useSmsDeliveryStatusPollQuery.graphql.js";import { useState, useEffect, useCallback } from "react";
import { fetchQuery, graphql } from "react-relay";

import appRelayEnvironment from "../../config/AppRelayEnvironment";

const POLL_INTERVAL_MS = 1000;
const PENDING_STATUS = "PENDING";
const SENT_STATUS = "SENT";
const FAILED_STATUS = "FAILED";

// Polls the delivery status of the SMS code the user is being challenged on, mirroring the
// server-rendered flow (smsDeliveryStatusPoll.js): it starts on mount, keeps polling while
// the send is PENDING, then stops once it settles — SENT (the cooldown countdown takes
// over from `occurredAt`) or FAILED (the failure message is shown). A resend resets the
// status server-side, so `restartPolling` re-arms a fresh polling cycle.
const useSmsDeliveryStatusPoll = () => {
  const [delivery, setDelivery] = useState(null);
  const [pollCycle, setPollCycle] = useState(0);

  useEffect(() => {
    let interval;

    const pollDeliveryStatus = async () => {
      try {
        const data = await fetchQuery(
          appRelayEnvironment,
          useSmsDeliveryStatusPollQuery,
          {},
          { fetchPolicy: "network-only" }
        ).toPromise();

        const status = data?.twoFactorSmsDeliveryStatus ?? null;
        setDelivery(status);

        // Stop once the send settled: the countdown and the failure message are driven
        // from this last value, so there is nothing left to poll for.
        if (status?.status !== PENDING_STATUS) {
          clearInterval(interval);
        }
      } catch {
        // A failed poll is not actionable; the next tick will retry.
      }
    };

    pollDeliveryStatus();
    interval = setInterval(pollDeliveryStatus, POLL_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [pollCycle]);

  // Re-arm polling after a resend, optimistically showing PENDING until the first poll of
  // the new cycle confirms it (the resend already reset the status server-side).
  const restartPolling = useCallback(() => {
    setDelivery({ status: PENDING_STATUS, occurredAt: null });
    setPollCycle(cycle => cycle + 1);
  }, []);

  return {
    deliveryPending: delivery?.status === PENDING_STATUS,
    deliveryFailed: delivery?.status === FAILED_STATUS,
    sentAt: delivery?.status === SENT_STATUS ? delivery.occurredAt : null,
    restartPolling
  };
};

export default useSmsDeliveryStatusPoll;

const useSmsDeliveryStatusPollQuery = graphql__f5c1293509ea94c6ccbc;
